import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Icon } from '@iconify/react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useRequestState } from '@app/hooks';
import { FormProvider, RHFCheckbox, RHFTextField } from '@app/components';
import { IChecklist } from '../types';
import {
  addCheckListItem,
  deleteCheckList,
  deleteCheckListItem,
  getCheckListItem,
  updateCheckList,
  updateItemStatus,
} from '../slice';
import {
  selectAddCheckListItemState,
  selectDeleteCheckListState,
  selectUpdateItemStatus,
} from '../selector';
import { Strings } from '@app/constants';

interface IProps {
  check: IChecklist;
  key: number;
}

const AddCheckListItem = ({ check, key }: IProps) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isAddingMap, setIsAddingMap] = useState<Record<number, boolean>>({});
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editItemText, setEditItemText] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);

  const theme = useTheme();
  const dispatch = useDispatch();

  const methods = useForm({
    defaultValues: {
      checkListItem: '',
      title: check?.text || '',
      items:
        check?.items?.map((item) => ({
          id: item.id,
          label: item.title,
          checked: item.isChecked,
        })) || [],
    },
  });
  const { handleSubmit, setValue, getValues } = methods;

  useEffect(() => {
    if (check?.items) {
      setValue(
        'items',
        check.items.map((item) => ({
          id: item.id,
          label: item.title,
          checked: item.isChecked,
        }))
      );
    }
  }, [check?.items, setValue]);

  useRequestState({
    stateSelector: selectDeleteCheckListState,
    successMessageShown: true,
    errorShown: true,
  });

  const { loading } = useRequestState({
    stateSelector: selectAddCheckListItemState,
    errorShown: true,
  });

  const handleAddItemSuccess = useCallback(() => {
    setIsAddingMap((prev) => ({ ...prev, [check.id]: false }));
    setValue('checkListItem', '');
    dispatch(getCheckListItem({ checklistId: check?.id }));
  }, [check.id, setValue, dispatch]);

  useRequestState({
    stateSelector: selectUpdateItemStatus,
    errorShown: true,
    onSuccess: handleAddItemSuccess,
  });

  const handleAddItem = (data: any) => {
    const trimmedTitle = data?.checkListItem?.trim();

    if (!trimmedTitle) return;

    dispatch(
      addCheckListItem({
        checkListId: check?.id,
        title: trimmedTitle,
        isChecked: false,
      })
    );
    setValue('checkListItem', '');
  };

  useEffect(() => {
    dispatch(getCheckListItem({ checklistId: check?.id }));
  }, [check?.id, dispatch]);

  const handleTitleSubmit = () => {
    const updatedText = getValues('title');
    dispatch(
      updateCheckList({
        checkListId: check?.id,
        text: updatedText,
        isCompleted: true,
      })
    );
    setIsEditingTitle(false);
  };

  const checkedCount = check?.items?.filter((item) => item.isChecked).length ?? 0;
  const totalCount = check?.items?.length ?? 1;
  const progress = (checkedCount / totalCount) * 100;

  const handleItemUpdate = (itemId: number) => {
    const trimmed = editItemText.trim();
    const item = check.items.find((i) => i.id === itemId);
    dispatch(
      updateItemStatus({
        itemId,
        title: trimmed,
        isChecked: item?.isChecked ?? false,
      })
    );
    setEditingItemId(null);
    setEditItemText('');
  };

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const renderTextWithLinks = (text: string, theme: any) => {
    const urlRegexGlobal = /(https?:\/\/[^\s]+)/g;
    const urlRegex = /^https?:\/\/[^\s]+$/;
    const parts = text?.split(urlRegexGlobal);

    return parts?.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <MuiLink
            key={`link-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'blue',
              textDecoration: 'underline',
            }}
          >
            {part}
          </MuiLink>
        );
      }
      return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    });
  };

  return (
    <FormProvider methods={methods} key={key}>
      <Box sx={{ p: 2, mt: 1, backgroundColor: theme.palette.common.white, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckBoxOutlinedIcon fontSize="small" />
            {isEditingTitle ? (
              <RHFTextField
                name="title"
                size="small"
                variant="standard"
                autoFocus
                onBlur={handleTitleSubmit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleTitleSubmit();
                  }
                }}
              />
            ) : (
              <Typography
                fontWeight={600}
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setIsEditingTitle(true);
                }}
              >
                {check?.text}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" alignItems="center">
            <IconButton
              size="small"
              color="inherit"
              onClick={() => {
                dispatch(deleteCheckList({ checkListId: check?.id }));
              }}
            >
              <DeleteIcon style={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              color="inherit"
              onClick={handleToggleExpanded}
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            >
              <ExpandMoreIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
          <Typography variant="caption">{`${Math?.round(Number(progress) || 0)}%`}</Typography>
          <LinearProgress
            variant="determinate"
            value={Number.isNaN(progress) ? 0 : progress}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 5,
              backgroundColor: theme.palette.grey[300],
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.taskboard.darkSlateGray,
                borderRadius: 5,
              },
            }}
          />
        </Stack>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {check?.items?.length > 0 &&
              check?.items?.map((item, index) => (
                <Stack
                  key={item.id}
                  flexDirection={'row'}
                  justifyContent={'space-between'}
                  alignItems={'flex-start'}
                >
                  <RHFCheckbox
                    name={`item-${item.id}-checked`}
                    label={
                      editingItemId === item.id ? (
                        <RHFTextField
                          name={`item-${item.id}-title`}
                          value={editItemText}
                          onChange={(e) => {
                            setEditItemText(e.target.value);
                          }}
                          onBlur={() => {
                            handleItemUpdate(item.id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleItemUpdate(item.id);
                            }
                          }}
                          autoFocus
                          style={{
                            fontSize: '0.875rem',
                            width: '100%',
                            padding: '4px 8px',
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        <Box
                          component="span"
                          sx={{
                            mt: 1,
                            display: 'inline-block',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          {renderTextWithLinks(item?.title, theme)}
                        </Box>
                      )
                    }
                    checked={item?.isChecked}
                    FormControlLabelProps={{
                      sx: {
                        alignItems: 'flex-start',
                      },
                    }}
                    sx={{
                      color: theme.palette.taskboard.darkSlateGray,
                      '&.Mui-checked': {
                        color: theme.palette.taskboard.darkSlateGray,
                      },
                    }}
                    onChange={(_, checked) => {
                      dispatch(
                        updateItemStatus({
                          itemId: item.id,
                          title: item.title,
                          isChecked: checked,
                        })
                      );
                    }}
                  />
                  <Stack flexDirection={'row'}>
                    <IconButton
                      color="inherit"
                      onClick={() => {
                        setEditingItemId(item.id);
                        setEditItemText(item.title);
                      }}
                      style={{ fontSize: 18 }}
                    >
                      <Icon icon={'flowbite:edit-outline'} style={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      color="inherit"
                      onClick={() => {
                        dispatch(deleteCheckListItem({ itemId: item?.id }));
                      }}
                    >
                      <DeleteIcon style={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>
                </Stack>
              ))}
          </Stack>

          {isAddingMap[check.id] ? (
            <>
              <RHFTextField
                name="checkListItem"
                placeholder="Add an item"
                fullWidth
                sx={{ mt: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleSubmit(handleAddItem)();
                  }
                }}
              />
              <Stack sx={{ display: 'flex', flexDirection: 'row', mt: 1, gap: 1 }}>
                <LoadingButton
                  variant="contained"
                  size="small"
                  onClick={() => {
                    void handleSubmit(handleAddItem)();
                  }}
                  loading={loading}
                  sx={{
                    backgroundColor: theme.palette.taskboard.darkSlateGray,
                    '&:hover': {
                      backgroundColor: theme.palette.taskboard.darkSlateGray,
                    },
                  }}
                >
                  {Strings.button.add}
                </LoadingButton>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setValue('checkListItem', '');
                    setIsAddingMap((prev) => ({ ...prev, [check.id]: false }));
                  }}
                  sx={{
                    color: theme.palette.taskboard.darkSlateGray,
                    borderColor: theme.palette.taskboard.darkSlateGray,
                    '&:hover': {
                      color: theme.palette.taskboard.darkSlateGray,
                      borderColor: theme.palette.taskboard.darkSlateGray,
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  {Strings.button.cancel}
                </Button>
              </Stack>
            </>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setIsAddingMap((prev) => ({ ...prev, [check.id]: true }));
              }}
              sx={{
                mt: 2,
                color: theme.palette.taskboard.darkSlateGray,
                borderColor: theme.palette.taskboard.darkSlateGray,
                '&:hover': {
                  color: theme.palette.taskboard.darkSlateGray,
                  borderColor: theme.palette.taskboard.darkSlateGray,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {Strings.taskBoard.addItem}
            </Button>
          )}
        </Collapse>
      </Box>
    </FormProvider>
  );
};

export { AddCheckListItem };
